import { useEffect } from 'react';
import { useModal } from '@ayocore/platform/client/hooks/useModal';
import { CrmCreateTaskForm, isTaskEntityType } from '../../components/tasks/CrmCreateTaskForm';
import type {
  ActivitySlashCommandContext,
  ActivitySlashCommandFormProps,
} from '@ayocore/activity/client/extensions';
import { registerActivityChatCommand } from '@ayocore/activity/client/extensions';

/**
 * Резолвит entity для задачи: scope комментария важнее треда (учёт reply на конкретную сущность).
 */
function resolveTaskTargetEntity(context: ActivitySlashCommandContext) {
  const commentScope = context.source === 'comment' ? context.event?.scope : null;
  if (commentScope?.entity_type && commentScope.entity_uuid) {
    return { entityType: commentScope.entity_type, entityUuid: commentScope.entity_uuid };
  }
  return {
    entityType: context.threadEntityType ?? null,
    entityUuid: context.threadEntityUuid ?? null,
  };
}

/**
 * Surface слэш-команды `/task` — открывает общую модалку `CrmCreateTaskForm` с
 * предзаполненной сущностью и закрывает slash-контекст при закрытии модалки.
 * Сам компонент ничего не рендерит inline.
 */
function TaskCommandLauncher({ context, onSubmitted }: ActivitySlashCommandFormProps) {
  const modal = useModal(CrmCreateTaskForm);

  useEffect(() => {
    const target = resolveTaskTargetEntity(context);
    if (!target.entityUuid || !isTaskEntityType(target.entityType)) {
      onSubmitted();
      return;
    }

    modal.onClose(() => onSubmitted());
    modal.open(
      {
        entityUuid: target.entityUuid,
        entityType: target.entityType,
        title: 'Новая задача',
        onCreated: () => modal.close(),
      },
      { inputSize: 'sm' }
    );

    return () => {
      modal.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return null;
}

/**
 * Команда `/task` — создание задачи в контексте текущего треда.
 */
export const taskActivityChatCommand = registerActivityChatCommand({
  id: 'create_task',
  trigger: 'task',
  label: 'Новая задача',
  description: 'Создать задачу для текущего партнера',
  aliases: ['create-task', 't'],
  comment: {
    label: 'Создать задачу',
  },
  isAvailable: context => Boolean(context.threadEntityType && context.threadEntityUuid),
  FormComponent: TaskCommandLauncher,
});
